import { useState, useCallback, useEffect } from "react";
  // Test service access
import Layer from "@arcgis/core/layers/Layer";
import WebMap from "@arcgis/core/WebMap";
import useBogsGisPortalAuth from "./useBogsGisPortalAuth";

export default function useBogsGisPortalAuthorize(isProductionFlow = false, options = {}) {
  const baseAuth = useBogsGisPortalAuth(isProductionFlow, options);

  const [privileges, setPrivileges] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [layerCheckResult, setLayerCheckResult] = useState(null);
  const [layerChecking, setLayerChecking] = useState(false);

  // Extract privileges and groups
  useEffect(() => {
    if (!baseAuth.portal || !baseAuth.signedIn) return;

    const user = baseAuth.portal.user;
    if (user) {
      setPrivileges(user.privileges || []);
      setUserGroups(
        (user.groups || []).map((g) => ({
          id: g.id,
          title: g.title,
          owner: g.owner,
        }))
      );
    }
  }, [baseAuth.portal, baseAuth.signedIn]);

  // Query Items safely
  const queryPortalItems = useCallback(
    async (queryFilter = 'type:"Web Map"', numResults = 20) => {
      if (!baseAuth.portal || !baseAuth.signedIn) {
        console.warn("Cannot query items: Portal instance or user session not ready.");
        return;
      }

      setItemsLoading(true);
      try {
        let rawFilter = typeof queryFilter === "string" && queryFilter.trim() 
          ? queryFilter.trim() 
          : 'type:"Web Map"';

        let queryString = rawFilter;

        // Resolve "owner:me"
        if (rawFilter === "owner:me") {
          const username = baseAuth.portal.user?.username;
          queryString = username ? `owner:"${username}"` : 'type:"Web Map"';
        }

        const queryParams = {
          query: queryString,
          num: numResults,
          sortField: "modified",
          sortOrder: "desc"
        };

        const res = await baseAuth.portal.queryItems(queryParams);

        const formattedResults = (res?.results || []).map((item) => ({
          id: item.id,
          title: item.title,
          type: item.type,
          owner: item.owner,
          access: item.access,
          url: item.url,
          created: item.created ? new Date(item.created).toLocaleDateString() : "N/A",
        }));

        setItems(formattedResults);
      } catch (err) {
        console.error("Error querying portal items:", err);
        setItems([]);
      } finally {
        setItemsLoading(false);
      }
    },
    [baseAuth.portal, baseAuth.signedIn]
  );





const testServiceAccess = async (targetUrl) => {
  setLayerChecking(true);
  try {
    // 1. WEB MAPS
    if (targetUrl.includes("webmap") || targetUrl.includes("/content/items/")) {
      const urlObj = new URL(targetUrl, window.location.href);
      const itemId = urlObj.searchParams.get("webmap") || targetUrl.split("/items/")[1]?.split("/")[0];

      const webmap = new WebMap({
        portalItem: { id: itemId }
      });

      await webmap.load();

      setLayerCheckResult({
        success: true,
        title: webmap.portalItem?.title || "Web Map Loaded Successfully",
        geometryType: "WebMap Container",
        canEdit: false
      });
    } 
    // 2. ALL SERVICES (Feature Services, Vector Tiles, Map Services, Image Services)
    else {
      // Layer.fromArcGISServerUrl automatically detects the correct Esri Layer class
      // (VectorTileLayer, FeatureLayer, MapImageLayer, TileLayer, etc.)
      const layer = await Layer.fromArcGISServerUrl({ url: targetUrl });
      await layer.load();

      setLayerCheckResult({
        success: true,
        title: layer.title || "Layer Loaded",
        geometryType: layer.geometryType || layer.type || "Layer Service",
        canEdit: layer.capabilities?.editing?.supportsUpdate || false
      });
    }
  } catch (err) {
    setLayerCheckResult({
      success: false,
      error: err.message || "Failed to load item/layer."
    });
  } finally {
    setLayerChecking(false);
  }
};
  return {
    ...baseAuth,
    privileges,
    userGroups,
    items,
    itemsLoading,
    queryPortalItems,
    layerCheckResult,
    layerChecking,
    testServiceAccess,
  };
}